import { Button, ButtonProps, CircularProgress, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Stack } from '@mui/system'

import imageCompression from 'browser-image-compression'
import { heicTo, isHeic } from 'heic-to'
import * as React from 'react'

const ColorButton = styled(Button)<ButtonProps>(() => ({
  '&:hover': {
    backgroundColor: '#e1e1e1',
    borderColor: '#636363',
  },
  backgroundColor: '#fff',
  border: '1px solid #636363',
  borderRadius: 100,
  color: '#636363',
  fontWeight: 700,
  letterSpacing: '0.03rem',
  textTransform: 'none',
}))

interface ImageUploadProps {
  fileName: string
  setFileName: (fileName: string) => void
  setImage: (image: string | ArrayBuffer | null) => void
  isProcessing: boolean
  setIsProcessing: (isProcessing: boolean) => void
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  fileName,
  setFileName,
  setImage,
  isProcessing,
  setIsProcessing,
}) => {
  const handleCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return // No file selected
    }

    let processedBlob: Blob = file
    let finalFileName = file.name

    try {
      setIsProcessing(true)
      // 1. Check if the file is HEIC/HEIF
      const fileIsHeic = await isHeic(file)

      if (fileIsHeic) {
        console.log('HEIC/HEIF detected, converting to JPEG...')
        processedBlob = (await heicTo({
          blob: file,
          quality: 0.9,
          type: 'image/jpeg',
        })) as Blob

        finalFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
      }

      // 2. Compress the image
      const options = {
        maxSizeMB: 3,
        maxWidthOrHeight: 2560, // Good balance for quality/resolution
        useWebWorker: true,
      }

      processedBlob = await imageCompression(processedBlob as File, options)

      // 3. Read the blob as Data URL
      const reader = new FileReader()
      reader.readAsDataURL(processedBlob)
      reader.onloadend = () => {
        setFileName(finalFileName)
        setImage(reader.result)
        setIsProcessing(false)
      }
      reader.onerror = (error) => {
        console.error('FileReader error:', error)
        setFileName('')
        setImage(null)
        setIsProcessing(false)
      }
    } catch (error) {
      console.error('Error during image processing:', error)
      setFileName('')
      setImage(null)
      setIsProcessing(false)
    } finally {
      event.target.value = ''
    }
  }

  return (
    <Stack>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Upload a photo
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2}>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          id="file-input"
          onChange={handleCapture}
        />
        <label htmlFor="file-input" style={{ marginLeft: 0, width: '100%' }}>
          <ColorButton
            variant="outlined"
            component="span"
            tabIndex={-1}
            role={undefined}
            sx={{
              fontSize: { md: '14px', xs: '11px' },
              padding: { md: '5px 16px 5px 16px', xs: '5px 2px 5px 2px' },
            }}
            fullWidth
          >
            Take a photo
          </ColorButton>
        </label>
        <input type="file" accept="image/*" style={{ display: 'none' }} id="camera-input" onChange={handleCapture} />
        <label htmlFor="camera-input" style={{ width: '100%' }}>
          <ColorButton
            variant="outlined"
            tabIndex={-1}
            role={undefined}
            component="span"
            sx={{
              fontSize: { md: '14px', xs: '11px' },
              padding: { md: '5px 26px 5px 16px', xs: '6px 12px' },
            }}
            fullWidth
          >
            Camera roll
          </ColorButton>
        </label>
      </Stack>
      {isProcessing && (
        <Stack direction="row" alignItems="center" spacing={1} marginTop={2}>
          <CircularProgress size={20} color="primary" />
          <Typography variant="subtitle2" fontWeight={500} color="primary" gutterBottom>
            Processing image
          </Typography>
        </Stack>
      )}
      {fileName && (
        <Typography marginTop={2} variant="subtitle2" fontWeight={500} gutterBottom>
          {fileName}
        </Typography>
      )}
    </Stack>
  )
}

export default ImageUpload
